import { Check, Copy } from "lucide-react";
import { useEffect, useState } from "react";
import { getMusicSettings, type JobInput, type MusicSettings } from "../../api";
import { prettifyTrackName } from "../../lib/music";

/**
 * Renders the original job submission as a copyable list. Each row is
 * (label, value, copy button). Lets the operator grab the YouTube URL,
 * timestamps, or first-spoken phrase back out of any past job — useful
 * when iterating clip parameters or building a reference catalog.
 *
 * Falls back to the legacy sourceUrl-only display if the server didn't
 * hydrate input.json (older jobs created before that persistence
 * existed).
 *
 * Lives in the Details dialog's "How it was made" tab (spec 2026-09-16
 * §2.2); it used to sit under the page's preview.
 */
export function InputsPanel({
  input,
  fallbackUrl,
  jobId,
}: {
  input: JobInput;
  fallbackUrl?: string;
  jobId: string;
}): React.JSX.Element {
  // Pull the resolved music pick (what pickMusic actually settled on
  // for the latest render) so an "Auto" submission can still show
  // "Auto · resolved to X" once the render has run. One-shot fetch on
  // mount — the value only changes when a new render finishes, and the
  // surrounding job-detail page is already polling, so a re-mount on
  // status change picks up any update.
  const [resolvedMusic, setResolvedMusic] = useState<MusicSettings["resolved"]>(null);
  useEffect(() => {
    let cancelled = false;
    getMusicSettings(jobId)
      .then((s) => { if (!cancelled) setResolvedMusic(s.resolved); })
      .catch(() => { /* non-fatal — Music row falls back to submitted value */ });
    return () => { cancelled = true; };
  }, [jobId]);

  const url = input.url ?? fallbackUrl;
  const guests = (input.guests ?? []).join(", ");

  // Music display rules (tri-state input × tri-state resolution):
  //   operator picked X, resolved X  → "X" (renders match input — common)
  //   operator picked X, no resolved → "X" (pre-render — show their intent)
  //   Auto,            resolved Y    → "Auto · resolved to Y"
  //   Auto,            no resolved   → "Auto · picked for you" (pre-render)
  // So a re-cut that inherited the prior music shows the right title
  // both before pickMusic runs (from input.musicTrack) and after (from
  // music.json — they should agree).
  let musicDisplay: string;
  if (input.musicTrack && input.musicTrack.trim()) {
    musicDisplay = prettifyTrackName(input.musicTrack);
  } else if (resolvedMusic) {
    musicDisplay = `Auto · resolved to ${prettifyTrackName(resolvedMusic.track)}`;
  } else {
    musicDisplay = "Auto · picked for you";
  }

  const rows: Array<{ key: string; label: string; value: string | undefined; mono?: boolean }> = [
    { key: "url", label: "Source", value: url, mono: true },
    { key: "from", label: "Start", value: input.from, mono: true },
    { key: "to", label: "End", value: input.to, mono: true },
    {
      key: "duration",
      label: "Length",
      value: input.duration !== undefined ? `${input.duration}s` : undefined,
      mono: true,
    },
    { key: "phrase", label: "First words", value: input.startPhrase },
    { key: "endPhrase", label: "Last words", value: input.endPhrase },
    { key: "finePan", label: "Fine pan", value: input.finePan ? "on" : undefined },
    { key: "host", label: "Host", value: input.host },
    { key: "guests", label: "Guests", value: guests || undefined },
    { key: "topic", label: "Topic", value: input.topic },
    // Music is always shown — "Auto" is itself an operator-meaningful
    // choice, and the resolved-track hint when Auto is the most
    // useful piece of post-render info we can surface here.
    { key: "music", label: "Music", value: musicDisplay },
  ].filter((r) => r.value !== undefined && r.value !== "");
  if (rows.length === 0) return <></>;
  return (
    <div
      style={{
        marginTop: 4,
        padding: 14,
        borderRadius: 14,
        background: "rgba(16,18,27,.72)",
        border: "1px solid rgba(255,255,255,.06)",
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "var(--ql-fg-muted)",
          textTransform: "uppercase",
          letterSpacing: ".10em",
          marginBottom: 10,
        }}
      >
        What was asked for
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {rows.map((r) => (
          <CopyRow key={r.key} label={r.label} value={r.value!} mono={r.mono} />
        ))}
      </div>
    </div>
  );
}

function CopyRow({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  const handleCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch {
      /* clipboard blocked — silent fail is fine, user sees no checkmark */
    }
  };
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "82px 1fr 28px",
        alignItems: "center",
        gap: 10,
      }}
    >
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          color: "var(--ql-fg-subtle)",
          textTransform: "uppercase",
          letterSpacing: ".06em",
        }}
      >
        {label}
      </div>
      <div
        title={value}
        style={{
          fontSize: 12,
          color: "var(--ql-gray-200)",
          fontFamily: mono ? "var(--ql-font-mono)" : "inherit",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {value}
      </div>
      <button
        type="button"
        onClick={handleCopy}
        title={copied ? "Copied" : "Copy"}
        style={{
          width: 26,
          height: 26,
          borderRadius: 8,
          border: "1px solid rgba(255,255,255,.08)",
          background: copied ? "rgba(74,222,128,.12)" : "rgba(255,255,255,.02)",
          color: copied ? "var(--ql-success)" : "var(--ql-fg-muted)",
          cursor: "pointer",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all .15s var(--ql-ease)",
        }}
      >
        {copied ? <Check size={13} strokeWidth={2.5} /> : <Copy size={13} strokeWidth={1.85} />}
      </button>
    </div>
  );
}
